// ============================================
// 高德地图组件 - 支持真实地图显示和全屏
// ============================================

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Maximize2, Minimize2, Crosshair, Plus, Minus } from 'lucide-react';

interface AMapComponentProps {
  /** 地图中心点坐标 [lng, lat] */
  center?: [number, number];
  /** 缩放级别 */
  zoom?: number;
  /** 标记点列表 */
  markers?: Array<{
    position: [number, number];
    title?: string;
    content?: string;
    icon?: string;
    color?: string;
  }>;
  /** 是否显示热力图 */
  showHeatmap?: boolean;
  /** 热力图数据 */
  heatmapData?: Array<{ lng: number; lat: number; count: number }>;
  /** 是否支持点击选点 */
  enableClickToSelect?: boolean;
  /** 点击地图回调 */
  onMapClick?: (lng: number, lat: number, address?: string) => void;
  /** 地图高度 */
  height?: string;
  /** 是否允许全屏 */
  allowFullscreen?: boolean;
  /** 自定义样式类 */
  className?: string;
  /** 选中点坐标（用于显示选中效果） */
  selectedPoint?: { lng: number; lat: number } | null;
}

// 加载高德地图脚本（预留，需要配置真实API Key后使用）
/* 
const loadAMapScript = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (window.AMap) {
      resolve();
      return;
    }
    
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.src = 'https://webapi.amap.com/maps?v=2.0&key=YOUR_AMAP_KEY&plugin=AMap.Geocoder,AMap.Marker,AMap.Circle,AMap.Scale,AMap.ToolBar';
    script.onerror = () => reject(new Error('高德地图加载失败'));
    
    const timeout = setTimeout(() => {
      reject(new Error('高德地图加载超时'));
    }, 10000);
    
    script.onload = () => {
      clearTimeout(timeout);
      const checkAMap = () => {
        if (window.AMap) {
          resolve();
        } else {
          setTimeout(checkAMap, 100);
        }
      };
      checkAMap();
    };
    
    document.head.appendChild(script);
  });
};
*/

// 模拟地图组件（当无法加载真实地图时使用）
const MockMap: React.FC<AMapComponentProps & { isFullscreen: boolean; onToggleFullscreen: () => void }> = ({
  markers = [],
  center = [116.397428, 39.90923],
  zoom = 12,
  showHeatmap = false,
  enableClickToSelect = false,
  onMapClick,
  selectedPoint,
  height = '500px',
  isFullscreen,
  onToggleFullscreen,
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const [internalZoom, setInternalZoom] = useState(zoom);
  const [internalCenter, setInternalCenter] = useState(center);
  const [mapCenterLabel, setMapCenterLabel] = useState('北京市中心');

  // 模拟点击地图
  const handleMapClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!enableClickToSelect || !mapRef.current) return;
    
    const rect = mapRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width - 0.5) * 0.02 * (14 - internalZoom) + internalCenter[0];
    const y = ((e.clientY - rect.top) / rect.height - 0.5) * 0.02 * (14 - internalZoom) + internalCenter[1];
    
    // 模拟反向地理编码
    const areas = ['朝阳区', '海淀区', '东城区', '西城区', '丰台区', '通州区'];
    const streets = ['建国路', '中关村大街', '王府井大街', '西单北大街', '望京街', '三里屯路'];
    const randomArea = areas[Math.floor(Math.random() * areas.length)];
    const randomStreet = streets[Math.floor(Math.random() * streets.length)];
    const randomNum = Math.floor(Math.random() * 200) + 1;
    
    const mockAddress = `北京市${randomArea}${randomStreet}${randomNum}号`;
    
    if (onMapClick) {
      onMapClick(x, y, mockAddress);
    }
  };

  // 模拟定位
  const handleLocate = () => {
    // 模拟定位到当前位置
    const mockCurrentPos = [116.481488, 39.990464] as [number, number];
    setInternalCenter(mockCurrentPos);
    setMapCenterLabel('当前位置(望京SOHO)');
    if (onMapClick) {
      onMapClick(mockCurrentPos[0], mockCurrentPos[1], '北京市朝阳区望京街9号望京国际中心E座');
    }
  };

  return (
    <div 
      className={`relative w-full bg-gradient-to-br from-blue-50 via-green-50 to-yellow-50 overflow-hidden rounded-2xl border border-gray-200 ${isFullscreen ? 'fixed inset-0 z-50 rounded-none' : ''}`}
      style={{ height: isFullscreen ? '100vh' : height }}
    >
      {/* 模拟地图背景 */}
      <div 
        ref={mapRef}
        className="absolute inset-0 cursor-crosshair"
        onClick={handleMapClick}
        style={{
          backgroundImage: `
            linear-gradient(90deg, rgba(200,200,200,0.2) 1px, transparent 1px),
            linear-gradient(rgba(200,200,200,0.2) 1px, transparent 1px)
          `,
          backgroundSize: `${80 * (internalZoom / 10)}px ${80 * (internalZoom / 10)}px`,
        }}
      >
        {/* 中心点标记 */}
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
          <div className="w-4 h-4 bg-red-500 rounded-full border-2 border-white shadow-lg animate-pulse" />
          <div className="absolute top-6 left-1/2 transform -translate-x-1/2 whitespace-nowrap bg-white/90 px-2 py-1 rounded text-xs font-medium shadow">
            {mapCenterLabel}
          </div>
        </div>

        {/* 模拟区域标签 */}
        {['朝阳区', '海淀区', '东城区', '西城区'].map((area, i) => (
          <div 
            key={area}
            className="absolute px-3 py-1.5 bg-white/80 backdrop-blur rounded-lg text-sm font-medium text-gray-600 shadow-sm"
            style={{ 
              left: `${20 + i * 20}%`, 
              top: `${15 + (i % 2) * 30}%`,
            }}
          >
            {area}
          </div>
        ))}

        {/* 模拟热力图 */}
        {showHeatmap && (
          <div className="absolute inset-0 pointer-events-none">
            {[
              { x: 30, y: 40, intensity: 0.8 },
              { x: 60, y: 30, intensity: 0.6 },
              { x: 45, y: 60, intensity: 0.4 },
              { x: 70, y: 50, intensity: 0.7 },
            ].map((heat, i) => (
              <div
                key={i}
                className="absolute rounded-full"
                style={{
                  left: `${heat.x}%`,
                  top: `${heat.y}%`,
                  width: '120px',
                  height: '120px',
                  transform: 'translate(-50%, -50%)',
                  background: `radial-gradient(circle, rgba(239,68,68,${heat.intensity * 0.5}) 0%, transparent 70%)`,
                }}
              />
            ))}
          </div>
        )}

        {/* 标记点 */}
        {markers.map((marker, index) => {
          const x = ((marker.position[0] - internalCenter[0]) / 0.02 * (14 - internalZoom) + 0.5) * 100;
          const y = ((marker.position[1] - internalCenter[1]) / 0.02 * (14 - internalZoom) + 0.5) * 100;
          
          // 只在可视范围内显示
          if (x < -10 || x > 110 || y < -10 || y > 110) return null;
          
          return (
            <div
              key={index}
              className="absolute transform -translate-x-1/2 -translate-y-1/2 group z-10"
              style={{ left: `${x}%`, top: `${y}%` }}
            >
              <div 
                className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold shadow-lg cursor-pointer transition-transform group-hover:scale-110 ${
                  marker.color || 'bg-blue-500'
                }`}
              >
                {marker.icon || index + 1}
              </div>
              {marker.title && (
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-20">
                  <p className="font-bold">{marker.title}</p>
                  {marker.content && <p className="text-gray-300">{marker.content}</p>}
                  <div className="absolute top-full left-1/2 -translate-x-1/2 w-2 h-2 bg-gray-800 rotate-45" />
                </div>
              )}
            </div>
          );
        })}

        {/* 选中的点 */}
        {selectedPoint && (
          <div
            className="absolute transform -translate-x-1/2 -translate-y-1/2 z-20"
            style={{
              left: `${((selectedPoint.lng - internalCenter[0]) / 0.02 * (14 - internalZoom) + 0.5) * 100}%`,
              top: `${((selectedPoint.lat - internalCenter[1]) / 0.02 * (14 - internalZoom) + 0.5) * 100}%`,
            }}
          >
            <div className="absolute inset-0 bg-[#C41E3A] rounded-full animate-ping opacity-25" />
            <div className="relative w-6 h-6 bg-[#C41E3A] rounded-full border-2 border-white shadow-lg flex items-center justify-center">
              <div className="w-2 h-2 bg-white rounded-full" />
            </div>
            <div className="absolute -top-8 left-1/2 -translate-x-1/2 px-3 py-1 bg-[#C41E3A] text-white text-xs rounded-lg whitespace-nowrap font-medium">
              拟选位置
              <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-[#C41E3A] rotate-45" />
            </div>
          </div>
        )}
      </div>

      {/* 地图控制按钮 */}
      <div className="absolute top-4 right-4 z-20 flex flex-col gap-2">
        <button
          onClick={onToggleFullscreen}
          className="w-10 h-10 bg-white rounded-xl shadow-md flex items-center justify-center text-gray-600 hover:text-[#C41E3A] hover:shadow-lg transition-all"
          title={isFullscreen ? '退出全屏' : '全屏'}
        >
          {isFullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
        </button>
        <button
          onClick={handleLocate}
          className="w-10 h-10 bg-white rounded-xl shadow-md flex items-center justify-center text-gray-600 hover:text-[#C41E3A] hover:shadow-lg transition-all"
          title="定位到当前位置"
        >
          <Crosshair className="w-5 h-5" />
        </button>
        <button
          onClick={() => setInternalZoom(z => Math.min(z + 1, 18))}
          className="w-10 h-10 bg-white rounded-xl shadow-md flex items-center justify-center text-gray-600 hover:text-[#C41E3A] hover:shadow-lg transition-all"
          title="放大"
        >
          <Plus className="w-5 h-5" />
        </button>
        <button
          onClick={() => setInternalZoom(z => Math.max(z - 1, 3))}
          className="w-10 h-10 bg-white rounded-xl shadow-md flex items-center justify-center text-gray-600 hover:text-[#C41E3A] hover:shadow-lg transition-all"
          title="缩小"
        >
          <Minus className="w-5 h-5" />
        </button>
      </div>

      {/* 左下角图例 */}
      <div className="absolute bottom-4 left-4 bg-white/95 backdrop-blur rounded-xl p-3 shadow-lg z-20">
        <p className="text-xs font-medium text-gray-700 mb-2">图例</p>
        <div className="space-y-1.5">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-[#C41E3A] rounded-full" />
            <span className="text-xs text-gray-600">选中位置</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-blue-500 rounded-full" />
            <span className="text-xs text-gray-600">客户门店</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-red-500 rounded-full" />
            <span className="text-xs text-gray-600">竞争对手</span>
          </div>
          {showHeatmap && (
            <>
              <div className="border-t border-gray-200 my-2" />
              <p className="text-xs text-gray-500 mb-1">销售热力</p>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full bg-red-400/50" />
                <span className="text-xs text-gray-500">高</span>
                <div className="w-3 h-3 rounded-full bg-yellow-400/50" />
                <span className="text-xs text-gray-500">中</span>
                <div className="w-3 h-3 rounded-full bg-green-400/50" />
                <span className="text-xs text-gray-500">低</span>
              </div>
            </>
          )}
        </div>
      </div>

      {/* 地图来源标注 */}
      <div className="absolute bottom-2 right-2 text-[10px] text-gray-400 bg-white/80 px-2 py-0.5 rounded">
        模拟地图 (高德地图API待配置)
      </div>
    </div>
  );
};

// 主组件
export const AMapComponent: React.FC<AMapComponentProps> = (props) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  // 地图加载状态（预留）
  // const [useRealMap, setUseRealMap] = useState(false);
  // const [loadError, setLoadError] = useState(false);

  // 处理全屏切换
  const toggleFullscreen = useCallback(() => {
    setIsFullscreen(prev => !prev);
    
    if (!isFullscreen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  }, [isFullscreen]);

  // ESC键退出全屏
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isFullscreen) {
        setIsFullscreen(false);
        document.body.style.overflow = '';
      }
    };
    
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isFullscreen]);

  // 目前使用模拟地图（因为需要配置真实的API Key）
  return (
    <MockMap 
      {...props} 
      isFullscreen={isFullscreen} 
      onToggleFullscreen={toggleFullscreen}
    />
  );
};

// 声明全局AMap类型
declare global {
  interface Window {
    AMap: any;
  }
}

export default AMapComponent;
